import React, { useEffect, useState } from 'react';
import { ChevronDownIcon } from '../chat/SidebarIcons';

export const TASK_PROGRESS_PRESETS = [0, 25, 50, 75, 100];

export const normalizeTaskProgress = (value, status) => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return Math.max(0, Math.min(100, Math.round(value)));
  }
  if (status === 'COMPLETED') return 100;
  return 0;
};

/**
 * Compact progress bar + preset chips for partial task completion.
 * Collapsed by default in list views; expand via the chevron to update progress.
 */
const TaskProgressControl = ({
  value = 0,
  onChange,
  disabled = false,
  compact = false,
  showInput = true,
  className = '',
  collapsible = true,
  defaultExpanded = false,
}) => {
  const pct = normalizeTaskProgress(value);
  const [draft, setDraft] = useState(String(pct));
  const [expanded, setExpanded] = useState(() =>
    collapsible ? defaultExpanded : true
  );

  useEffect(() => {
    setDraft(String(pct));
  }, [pct]);

  const commitDraft = () => {
    const parsed = parseInt(draft, 10);
    if (Number.isNaN(parsed)) {
      setDraft(String(pct));
      return;
    }
    const next = normalizeTaskProgress(parsed);
    setDraft(String(next));
    if (next !== pct) {
      onChange?.(next);
    }
  };

  const showDetails = !collapsible || expanded;

  return (
    <div
      className={`min-w-0 ${className}`}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className={`inline-flex items-center gap-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-700 dark:hover:text-gray-200 transition-colors ${
            compact ? 'px-0.5 py-0.5' : 'px-1 py-1'
          }`}
          aria-expanded={expanded}
          title={expanded ? 'Collapse progress' : 'Expand progress'}
          aria-label={expanded ? 'Collapse progress' : `Expand progress (${pct}%)`}
        >
          <span
            className={`text-[10px] font-semibold tabular-nums ${
              pct > 0
                ? 'text-primary-700 dark:text-primary-300'
                : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            {pct}%
          </span>
          <span className="text-[10px] font-medium">Progress</span>
          <ChevronDownIcon
            className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${
              expanded ? '' : '-rotate-90'
            }`}
          />
        </button>
      )}

      {showDetails && (
        <div className={collapsible ? (compact ? 'mt-1' : 'mt-1.5') : ''}>
          <div className={`flex items-center gap-2 ${compact ? 'mb-1' : 'mb-1.5'}`}>
            <div className="flex-1 min-w-0 h-1.5 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  pct >= 100 ? 'bg-green-500' : pct > 0 ? 'bg-primary-500' : 'bg-transparent'
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-[10px] font-semibold tabular-nums text-gray-600 dark:text-gray-300 w-8 text-right flex-shrink-0">
              {pct}%
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {TASK_PROGRESS_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                disabled={disabled || pct === preset}
                onClick={() => onChange?.(preset)}
                className={`px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  pct === preset
                    ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-500'
                    : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary-400 hover:text-primary-700 dark:hover:text-primary-300'
                }`}
                title={`Set progress to ${preset}%`}
                aria-label={`Set progress to ${preset}%`}
              >
                {preset}%
              </button>
            ))}
            {showInput && (
              <input
                type="number"
                min={0}
                max={100}
                value={draft}
                disabled={disabled}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commitDraft}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitDraft();
                  }
                }}
                className="w-12 px-1 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
                aria-label="Custom completion percentage"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskProgressControl;
